import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';
import { RulesTable } from '../common/RulesTable';
import { 
  CreateRuleModal, 
  EditRuleModal, 
  ViewRuleModal, 
  DeleteRuleModal 
} from '../common/RuleModals';
import '../styles/ContentComponents.css';

function Rules() {
  const { showSuccess, showError } = useToast();
  const [rules, setRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    project_id: '',
    stage: '',
    active: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    project_id: '',
    name: '',
    rule: '',
    stage: '',
    active: true
  });
  const [editForm, setEditForm] = useState({
    name: '',
    rule: '',
    stage: '',
    active: true
  });

  // Loading states
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Stage options
  const stageOptions = ['event_store'];

  useEffect(() => {
    fetchRules();
    fetchProjects();
  }, []); // Only run on initial load

  useEffect(() => {
    // Debounce filter changes to prevent too many API calls
    const timeoutId = setTimeout(() => {
      fetchRulesWithFilters();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const fetchRules = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchRuleEngineDefinitions({});
      
      if (result.success) {
        const rulesData = result.data || [];
        setRules(rulesData);
        
        // Calculate statistics
        const total = rulesData.length;
        const active = rulesData.filter(rule => rule.active).length;
        const inactive = total - active;
        
        setStats({ total, active, inactive });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load rules');
      console.error('Failed to fetch rules:', err);
    }
    
    setLoading(false);
  };

  const fetchRulesWithFilters = async () => {
    setFilterLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchRuleEngineDefinitions(filters);
      
      if (result.success) {
        const rulesData = result.data || [];
        setRules(rulesData);
        
        // Calculate statistics
        const total = rulesData.length;
        const active = rulesData.filter(rule => rule.active).length;
        const inactive = total - active;
        
        setStats({ total, active, inactive });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load rules');
      console.error('Failed to fetch rules:', err);
    }
    
    setFilterLoading(false);
  };

  const fetchProjects = async () => {
    try {
      const result = await apiService.fetchProjects();
      
      if (result.success) {
        setProjects(result.data || []);
      } else {
        console.error('Failed to fetch projects:', result.error);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const handleRetry = () => {
    // If we have active filters, use filtered fetch, otherwise use regular fetch
    if (Object.values(filters).some(v => v !== '' && v !== null)) {
      fetchRulesWithFilters();
    } else {
      fetchRules();
    }
  };

  const handleCreateClick = () => {
    setCreateForm({
      project_id: '',
      name: '',
      rule: '',
      stage: '',
      active: true
    });
    setCreateModalOpen(true);
  };

  const handleEditClick = (rule) => {
    setSelectedRule(rule);
    
    // Format the rule for display in the textarea
    let formattedRule;
    try {
      // If rule is already an object, stringify it for display
      if (typeof rule.rule === 'object') {
        formattedRule = JSON.stringify(rule.rule, null, 2);
      } else {
        // If it's a string, try to parse and re-stringify for consistent formatting
        const parsed = JSON.parse(rule.rule);
        formattedRule = JSON.stringify(parsed, null, 2);
      }
    } catch (error) {
      // If parsing fails, use the original rule as is
      formattedRule = rule.rule;
    }
    
    setEditForm({
      name: rule.name || '',
      rule: formattedRule,
      stage: rule.stage || '',
      active: rule.active !== undefined ? rule.active : true
    });
    setEditModalOpen(true);
  };

  const handleViewClick = (rule) => {
    setSelectedRule(rule);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (rule) => {
    setSelectedRule(rule);
    setDeleteModalOpen(true);
  };

  const handleCreateRule = async () => {
    setCreateLoading(true);

    try {
      // Validate JSON
    let parsedRule;
    try {
      parsedRule = JSON.parse(createForm.rule);
    } catch (error) {
        showError('Invalid JSON format in rule definition');
        setCreateLoading(false);
      return;
    }

      const createData = {
        project_id: createForm.project_id,
        name: createForm.name,
        rule: parsedRule,
        stage: createForm.stage,
        active: createForm.active
      };
      
      const result = await apiService.createRuleEngineDefinition(createData);
      
      if (result.success) {
        showSuccess('Rule created successfully');
        setCreateModalOpen(false);
        fetchRules(); // Refresh the list
        } else {
        showError(result.error);
      }
    } catch (err) {
      showError('Failed to create rule');
      console.error('Failed to create rule:', err);
    }
    
    setCreateLoading(false);
  };

  const handleUpdateRule = async () => {
    setEditLoading(true);

    try {
      // Validate JSON
    let parsedRule;
    try {
      parsedRule = JSON.parse(editForm.rule);
    } catch (error) {
        showError('Invalid JSON format in rule definition');
        setEditLoading(false);
        return;
      }

      const updateData = {
        name: editForm.name,
        rule: parsedRule,
        stage: editForm.stage,
        active: editForm.active
      };
      
      const result = await apiService.updateRuleEngineDefinition(selectedRule.unique_id, updateData);
      
      if (result.success) {
        showSuccess('Rule updated successfully');
        setEditModalOpen(false);
        fetchRules(); // Refresh the list
        } else {
        showError(result.error);
      }
    } catch (err) {
      showError('Failed to update rule');
      console.error('Failed to update rule:', err);
    }
    
    setEditLoading(false);
  };

  const handleDeleteRule = async () => {
    setDeleteLoading(true);
    
    try {
      const result = await apiService.deleteRuleEngineDefinition(selectedRule.unique_id);
      
      if (result.success) {
        showSuccess('Rule deleted successfully');
        setDeleteModalOpen(false);
        fetchRules(); // Refresh the list
        } else {
        showError(result.error);
      }
    } catch (err) {
      showError('Failed to delete rule');
      console.error('Failed to delete rule:', err);
    }
    
    setDeleteLoading(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      project_id: '',
      stage: '',
      active: null
    });
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.unique_id === projectId);
    return project ? project.organization_name : projectId || 'Unknown Project';
  };

  const getStageDescription = (stage) => {
    const descriptions = {
      'event_store': 'Rules applied during event storage'
    };
    return descriptions[stage] || 'No description available';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (active) => {
    return active ? 'success' : 'error';
  };

  const getStageColor = (stage) => {
    const colors = {
      'pre_processing': 'primary',
      'processing': 'secondary',
      'post_processing': 'info',
      'validation': 'warning',
      'notification': 'success',
      'event_store': 'primary'
    };
    return colors[stage] || 'default';
  };

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: 3,
      px: 2
    }}>
      <RulesTable
        rules={rules}
        loading={loading}
        filterLoading={filterLoading}
        error={error}
        stats={stats}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        onRetry={handleRetry}
        onCreateClick={handleCreateClick}
        onEditClick={handleEditClick}
        onViewClick={handleViewClick}
        onDeleteClick={handleDeleteClick}
        getProjectName={getProjectName}
        getStageColor={getStageColor}
        getStatusColor={getStatusColor}
        formatDate={formatDate}
        stageOptions={stageOptions}
        projects={projects}
        showCreateButton={true}
        showStats={true}
        title="Rule Engine Definitions"
        maxWidth="1200px"
      />

      {/* Modals */}
      <CreateRuleModal
        open={createModalOpen} 
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateRule}
        form={createForm}
        setForm={setCreateForm}
        loading={createLoading}
        projects={projects}
        stageOptions={stageOptions}
        getStageDescription={getStageDescription}
      />

      <EditRuleModal
        open={editModalOpen} 
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateRule}
        form={editForm}
        setForm={setEditForm}
        loading={editLoading}
        stageOptions={stageOptions}
        getStageDescription={getStageDescription}
      />

      <ViewRuleModal
        open={viewModalOpen} 
        onClose={() => setViewModalOpen(false)}
        onEdit={() => {
              setViewModalOpen(false);
              handleEditClick(selectedRule);
            }}
        rule={selectedRule}
        getProjectName={getProjectName}
        getStageColor={getStageColor}
        getStatusColor={getStatusColor}
        formatDate={formatDate}
      />

      <DeleteRuleModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteRule}
        rule={selectedRule}
        loading={deleteLoading}
      />
    </Box>
  );
}

export default Rules; 